import { Controller } from "stimulus";

// Default color palette matching the former Chartkick.options[:colors]
const DEFAULT_COLORS = [
  "#29c884", "#dc1b4d", "#470FF4", "#f97a1e",
  "#4056F4", "#f65f5a", "#6c5be7", "#ee5768", "#8880ff"
];

export default class extends Controller {
  static values = {
    series: String,
    url: String,
    type: { type: String, default: "line" },
    height: { type: String, default: "300px" },
    colors: String,
    stacked: { type: Boolean, default: false },
    markers: { type: Boolean, default: false },
    smooth: { type: Boolean, default: false },
    min: { type: Number, default: undefined },
    suffix: { type: String, default: "" },
    prefix: { type: String, default: "" },
    legend: String,  // "" | "bottom" | "false"
    timezone: { type: String, default: "" },
    sparkline: { type: Boolean, default: false },
  };

  connect() {
    this.chart = null;
    this._observer = null;

    if (this.hasUrlValue && this.urlValue) {
      this._fetchAndRender();
    } else if (this.hasSeriesValue && this.seriesValue) {
      const raw = JSON.parse(this.seriesValue);
      this._render(this._normalize(raw));
    }
  }

  disconnect() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  // --- Data normalization ---
  // Handles three shapes from our API:
  //   1. Date-keyed hash: {"2024-01-01": 5, ...}
  //   2. Bare pair-array: [["2024-01-01", 5], ...]  (single series, no name)
  //   3. Named-series array: [{name, data: [["2024-01-01", 5], ...] or {"date": val}}, ...]
  _normalize(raw) {
    // Shape 3: array of objects with a `name` key
    if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object" && raw[0] !== null && "name" in raw[0]) {
      return raw.map(series => ({
        name: series.name,
        data: this._pairsToTimestamps(this._dataToPairs(series.data))
      }));
    }

    // Shape 1: date-keyed hash (plain object, not array)
    if (!Array.isArray(raw) && typeof raw === "object" && raw !== null) {
      const pairs = Object.entries(raw);
      return [{ name: null, data: this._pairsToTimestamps(pairs) }];
    }

    // Shape 2: bare pair-array [[date, val], ...] or empty []
    if (Array.isArray(raw)) {
      return [{ name: null, data: this._pairsToTimestamps(raw) }];
    }

    return [];
  }

  // Convert a data value that may be a hash or pair-array into pair-array
  _dataToPairs(data) {
    if (Array.isArray(data)) return data;
    if (typeof data === "object" && data !== null) return Object.entries(data);
    return [];
  }

  // Convert [[date_string, value], ...] → [[timestamp_ms, value], ...]
  _pairsToTimestamps(pairs) {
    return pairs.map(([date, val]) => {
      const ts = typeof date === "number" ? date : Date.parse(date);
      return [ts, val === null ? null : Number(val)];
    });
  }

  // --- Rendering ---
  _render(seriesData) {
    const colors = this.hasColorsValue && this.colorsValue
      ? JSON.parse(this.colorsValue)
      : DEFAULT_COLORS;

    const height = parseInt(this.heightValue, 10) || 300;
    const isArea = this.typeValue === "area";
    const chartType = this.smoothValue ? (isArea ? "areaspline" : "spline") : (isArea ? "area" : "line");
    const showLegend = this.legendValue === "bottom" || this.legendValue === "true";
    const suffix = this.suffixValue || "";
    const prefix = this.prefixValue || "";

    const yAxisMin = (this.hasMinValue && this.minValue !== undefined) ? this.minValue : undefined;

    const labelFormatter = function() {
      return prefix + window.Highcharts.numberFormat(this.value, -1) + suffix;
    };

    const tooltipFormatter = function() {
      const x = window.Highcharts.dateFormat("%b %e, %Y", this.x);
      const points = this.points || [this];
      const rows = points.map(pt => {
        const name = pt.series.name && pt.series.name !== "null" ? `<span style="color:${pt.color}">●</span> ${pt.series.name}: ` : "";
        const val = prefix + window.Highcharts.numberFormat(pt.y, -1) + suffix;
        return `${name}<b>${val}</b>`;
      });
      return `<span style="font-size:10px">${x}</span><br/>${rows.join("<br/>")}`;
    };

    const hcSeries = seriesData.map((s, i) => ({
      name: s.name && s.name !== "null" ? s.name : undefined,
      type: chartType,
      data: s.data,
      color: colors[i % colors.length],
      marker: { enabled: this.markersValue },
      fillOpacity: isArea ? 0.1 : undefined,
    }));

    const sparkline = this.sparklineValue;

    const options = {
      chart: {
        height,
        margin: sparkline ? [5, 0, 5, 0] : undefined,
        animation: { easing: "easeInOutQuad", duration: 350 },
      },
      title: { text: null },
      credits: { enabled: false },
      colors,
      time: this.timezoneValue ? { timezone: this.timezoneValue } : {},
      xAxis: {
        type: "datetime",
        visible: !sparkline,
        lineColor: "transparent",
        tickColor: "transparent",
        labels: {
          enabled: !sparkline,
          style: { color: "#9b9da1", fontFamily: "Basis Grotesque Pro", fontSize: "11px", fontWeight: "bold" }
        },
      },
      yAxis: {
        title: { text: null },
        visible: !sparkline,
        min: yAxisMin,
        labels: {
          enabled: !sparkline,
          formatter: labelFormatter,
          style: { color: "#9b9da1", fontFamily: "Basis Grotesque Pro", fontSize: "11px", fontWeight: "bold" }
        },
        gridLineColor: sparkline ? "transparent" : "#e8e8e8",
      },
      tooltip: {
        enabled: !sparkline,
        shared: hcSeries.length > 1,
        useHTML: true,
        formatter: tooltipFormatter,
        backgroundColor: "#fff",
        borderColor: "#e0e0e0",
        borderRadius: 4,
        shadow: false,
      },
      legend: {
        enabled: showLegend,
        verticalAlign: "bottom",
      },
      plotOptions: {
        series: {
          stacking: this.stackedValue ? "normal" : undefined,
          marker: { enabled: this.markersValue },
        },
      },
      series: hcSeries,
    };

    this.element.style.height = this.heightValue;

    this.chart = window.Highcharts.chart(this.element, options);
    this._setupResizeObserver();
  }

  _setupResizeObserver() {
    if (typeof ResizeObserver === "undefined") return;

    let lastWidth = this.element.offsetWidth;

    this._observer = new ResizeObserver(() => {
      const currentWidth = this.element.offsetWidth;
      if (lastWidth === 0 && currentWidth > 0 && this.chart) {
        this.chart.reflow();
      }
      lastWidth = currentWidth;
    });

    this._observer.observe(this.element);
  }

  _fetchAndRender() {
    fetch(this.urlValue, {
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
    })
      .then(r => r.json())
      .then(data => this._render(this._normalize(data)))
      .catch(err => console.error("[window.Highcharts]", err));
  }
}
